/**
 * KinsCircle Phase 6C — Memory Cluster Card.
 *
 * Renders null when `memoryClusters` flag is OFF or no usable clusters.
 * Pure read-only render of `buildMemoryClusters()` — never persists.
 */

import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Layers, Sparkles, Users } from 'lucide-react';
import { isMemoryClustersEnabled } from '@/lib/ai/semanticFeatureFlags';
import { buildMemoryClusters } from '@/lib/ai/semanticIndexer';

const MemoryClusterCard: React.FC = () => {
  if (!isMemoryClustersEnabled()) return null;

  const clusters = useMemo(() => buildMemoryClusters(), []);
  if (!clusters.length) return null;

  return (
    <section className="py-12 px-4 bg-gradient-to-br from-fuchsia-50/40 via-white to-pink-50/40">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Layers className="w-5 h-5 text-fuchsia-700" />
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
            Memory Themes
          </h2>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-fuchsia-600 bg-fuchsia-100 px-2 py-0.5 rounded-full">
            Beta
          </span>
        </div>
        <p className="text-sm text-slate-600 mb-6 max-w-2xl">
          We grouped your family memories into themes based on what they share. Nothing
          is saved — these clusters live only in your browser and update as you add
          more memories.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clusters.slice(0, 9).map((c) => (
            <Card
              key={c.id}
              className="bg-white border border-fuchsia-100 hover:border-fuchsia-300 hover:shadow-md transition-all"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-fuchsia-600" />
                    {c.label}
                  </span>
                  <span className="text-xs font-normal text-slate-500">
                    {c.memoryIds.length} memor
                    {c.memoryIds.length === 1 ? 'y' : 'ies'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2.5">
                {(c.yearRange.from || c.yearRange.to) && (
                  <div className="text-xs text-slate-500">
                    {c.yearRange.from === c.yearRange.to
                      ? `Around ${c.yearRange.from}`
                      : `${c.yearRange.from ?? '—'} – ${c.yearRange.to ?? '—'}`}
                  </div>
                )}
                {c.topTerms.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {c.topTerms.slice(0, 4).map((t) => (
                      <Badge
                        key={t}
                        variant="secondary"
                        className="bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-[10px]"
                      >
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
                {c.sharedPeople.length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Users className="w-3.5 h-3.5 text-pink-500" />
                    <span className="truncate">
                      {c.sharedPeople.slice(0, 3).join(', ')}
                      {c.sharedPeople.length > 3 &&
                        ` +${c.sharedPeople.length - 3} more`}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="text-[11px] text-slate-400 mt-4">
          Themes are detected automatically from words and people you tagged. They
          never replace or rewrite your memories.
        </p>
      </div>
    </section>
  );
};

export default MemoryClusterCard;
